<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Certificate</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Register a new medical council certificate
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-certificate</v-icon>
                      New Certificate Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="goBack"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
              <!-- Doctor context chip — same as education details -->
              <v-row v-if="isDoctorContext && prefillDoctorName" class="ma-0 mt-1">
                <v-col class="pa-0">
                  <v-chip color="primary" outlined x-small>
                    <v-icon left x-small>mdi-account</v-icon>
                    For: {{ prefillDoctorName }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Basic Certificate Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-certificate</v-icon>
                    Certificate Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="doctors"
                        label="Doctor"
                        placeholder="Select doctor"
                        outlined
                        :rules="[(v) => !!v || 'Doctor is required']"
                        :error-messages="errors.user_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                        :disabled="isDoctorContext"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-doctor</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.council_name"
                        label="Medical Council Name"
                        placeholder="e.g., Maharashtra Medical Council"
                        outlined
                        :rules="[(v) => !!v || 'Council name is required']"
                        :error-messages="errors.council_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-domain</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.registration_number"
                        label="Registration Number"
                        placeholder="e.g., MMC12345"
                        outlined
                        :rules="[(v) => !!v || 'Registration number is required']"
                        :error-messages="errors.registration_number"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-numeric</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.registration_year"
                        label="Registration Year"
                        placeholder="e.g., 2020"
                        outlined
                        type="number"
                        :min="1900"
                        :max="new Date().getFullYear() + 1"
                        :error-messages="errors.registration_year"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Date Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-calendar-range</v-icon>
                    Date Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.expiry_date"
                        label="Expiry Date"
                        placeholder="Select expiry date"
                        outlined
                        type="date"
                        :error-messages="errors.expiry_date"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-clock</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.renewal_date"
                        label="Last Renewal Date"
                        placeholder="Select last renewal date"
                        outlined
                        type="date"
                        :error-messages="errors.renewal_date"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-refresh</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Certificate Upload Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-file-upload</v-icon>
                    Certificate Document
                  </h3>
                  
                  <v-row>
                    <v-col cols="12">
                      <v-file-input
                        v-model="certificateFile"
                        label="Upload Certificate Document"
                        placeholder="Choose PDF, JPG, or PNG file"
                        outlined
                        accept=".pdf,.jpg,.jpeg,.png"
                        :error-messages="errors.certificate_path"
                        class="professional-field"
                        hide-details="auto"
                        prepend-icon=""
                        show-size
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-file-pdf-box</v-icon>
                        </template>
                      </v-file-input>
                      <div class="text-caption grey--text mt-1">
                        Maximum file size: 5MB. Supported formats: PDF, JPG, PNG
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Status Information Section (Admin Only) -->
                <div class="form-section mb-2" v-if="canVerify">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-check-decagram</v-icon>
                    Verification Status
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="form.status"
                        label="Mark as Verified"
                        :error-messages="errors.status"
                        color="success"
                        hide-details="auto"
                      ></v-switch>
                      <div class="text-caption grey--text mt-1">
                        Only administrators can verify certificates
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="form.is_active"
                        label="Active Status"
                        :error-messages="errors.is_active"
                        color="primary"
                        hide-details="auto"
                      ></v-switch>
                      <div class="text-caption grey--text mt-1">
                        Inactive certificates won't be visible to users
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Action Buttons -->
                <div class="form-actions mt-4">
                  <v-row>
                    <v-col cols="12">
                      <div class="d-flex justify-space-between align-center">
                        <!-- Left Side: Reset Button -->
                        <v-btn
                          outlined
                          color="warning"
                          @click="resetForm"
                          :disabled="isLoading"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Reset Form
                        </v-btn>

                        <!-- Right Side: Save Buttons -->
                        <div class="d-flex">
                          <v-btn
                            outlined
                            color="primary"
                            @click="saveAsDraft"
                            :loading="isLoading"
                            :disabled="!form.council_name || !form.registration_number"
                            class="mr-2"
                          >
                            <v-icon left>mdi-content-save</v-icon>
                            Save as Draft
                          </v-btn>

                          <v-btn
                            color="primary"
                            @click="submitForm"
                            :loading="isLoading"
                            :disabled="!valid"
                          >
                            <v-icon left>mdi-check</v-icon>
                            Create Certificate
                          </v-btn>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>

              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-center">
          <v-icon color="success" size="48">mdi-check-circle</v-icon>
        </v-card-title>
        <v-card-text class="text-center">
          <h3 class="text-h6 mb-2">Certificate Created Successfully!</h3>
          <p class="grey--text">
            The certificate has been registered and {{form.status ? 'verified' : 'is pending verification'}}.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="goBack">Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CreateCouncilRegistrationCertificate",
  
  data() {
    return {
      valid: false,
      isLoading: false,
      successDialog: false,
      certificateFile: null,
      doctors: [],
      prefillDoctorName: null,

      form: {
        user_id: null,
        council_name: '',
        registration_number: '',
        registration_year: null,
        expiry_date: null,
        renewal_date: null,
        status: false,
        is_active: true,
      },
    };
  },

  computed: {
    canVerify() {
      return this.roleName === 'Super Admin' || this.roleName === 'Admin';
    },
    isDoctorContext() {
      // Either the explicit route name OR a doctorId param in the URL is enough.
      return (
        !!this.$route.params.doctorId ||
        this.$route.name === 'DoctorCouncilCertificateCreatePage' ||
        /\/doctors\/[^/]+\/council-certificates/.test(this.$route.path)
      );
    },
    doctorId() {
      return this.isDoctorContext ? this.$route.params.doctorId : null;
    },
  },

  async mounted() {
    await this.loadDoctors();

    // Only default to the logged-in user when NOT coming from a doctor's profile page
    // (loadDoctors already pre-fills form.user_id in the doctor context)
    if (!this.canVerify && !this.isDoctorContext) {
      this.form.user_id = this.user.id;
    }
  },

  methods: {
    async loadDoctors() {
      try {
        if (this.isDoctorContext) {
          // Fetch only the specific doctor using the encrypted route param
          const response = await axios.get(`/users/${this.doctorId}`);
          const doctor = response.data.data || response.data;

          const name = `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim();
          const label = doctor.email ? `${name} (${doctor.email})` : name;

          this.doctors = [{ text: label, value: doctor.id }];
          this.prefillDoctorName = label;
          this.form.user_id = doctor.id;
        } else {
          const response = await axios.get('/users', {
            params: { role_name: 'Doctor' }
          });
          this.doctors = response.data.data.map(doctor => ({
            text: `${doctor.first_name} ${doctor.last_name} (${doctor.email})`,
            value: doctor.id,
          }));
        }
      } catch (error) {
        console.error("Error loading doctors:", error);
        this.$toast.error("Failed to load doctors list");
      }
    },

    goBack() {
      this.$router.back();
    },

    async submitForm() {
      if (!this.$refs.form.validate()) {
        this.$toast.error("Please fill in all required fields correctly");
        return;
      }

      this.isLoading = true;

      try {
        const formData = new FormData();
        
        // Add form fields
        Object.keys(this.form).forEach(key => {
          if (this.form[key] !== null && this.form[key] !== '') {
            formData.append(key, this.form[key]);
          }
        });

        // Add certificate file if uploaded
        if (this.certificateFile) {
          formData.append('certificate_path', this.certificateFile);
        }

        await axios.post('/council_registration_certificates', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        this.successDialog = true;
        this.$toast.success("Certificate created successfully!");

      } catch (error) {
        console.error("Error creating certificate:", error);
        
        if (error.response?.status === 422) {
          this.$toast.error("Please check the form data and try again");
        } else {
          this.$toast.error("Failed to create certificate");
        }
      } finally {
        this.isLoading = false;
      }
    },

    async saveAsDraft() {
      this.form.is_active = false;
      await this.submitForm();
    },

    resetForm() {
      this.$refs.form.reset();
      this.certificateFile = null;
      // Preserve the prefilled doctor when in doctor context
      const preservedUserId = this.isDoctorContext
        ? this.form.user_id
        : (this.canVerify ? null : this.user.id);
      this.form = {
        user_id: preservedUserId,
        council_name: '',
        registration_number: '',
        registration_year: null,
        expiry_date: null,
        renewal_date: null,
        status: false,
        is_active: true,
      };
    },

    afterSuccess() {
      this.successDialog = false;
      this.goBack();
    },
  },
};
</script>

<style scoped>
.form-card {
  border-radius: 12px;
  overflow: hidden;
}

.page-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-bottom: 1px solid #e0e0e0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 0.875rem;
  opacity: 0.8;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.form-content {
  background-color: #fafafa;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.section-title {
  color: #424242;
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.professional-field >>> .v-input__control {
  border-radius: 8px;
}

.professional-field >>> .v-text-field__details {
  padding-left: 0;
  padding-right: 0;
}

.form-actions {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.v-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

/* File input styling */
.professional-field >>> .v-file-input .v-input__control .v-input__slot {
  border-radius: 8px;
}
</style>